import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { Calculator, Calendar, DollarSign, Trophy, Zap, Flame } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function DebtSimulator() {
  const [debts, setDebts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [payment, setPayment] = useState(200);
  const [result, setResult] = useState(null);

  // Race mode
  const [budget, setBudget] = useState(500);
  const [race, setRace] = useState(null);

  useEffect(() => {
    api.get("/accounts").then(r => {
      const d = r.data.filter(a => a.type === "credit_card" || a.type === "loan");
      setDebts(d);
      if (d.length) { setSelected(d[0]); setPayment(Math.max(100, Math.round(Math.abs(d[0].balance) / 24))); }
      const totalDebt = d.reduce((s, x) => s + Math.abs(x.balance), 0);
      setBudget(Math.max(300, Math.round(totalDebt / 36 / 25) * 25));
    });
  }, []);

  useEffect(() => {
    if (!selected) return;
    api.post("/debt/simulate", { account_id: selected.account_id, monthly_payment: payment })
      .then(r => setResult(r.data)).catch(() => {});
  }, [selected, payment]);

  useEffect(() => {
    if (!debts.length) return;
    api.post("/debt/race", { monthly_budget: budget }).then(r => setRace(r.data)).catch(() => {});
  }, [debts, budget]);

  if (!debts.length) return <div className="p-8">Loading debts...</div>;

  const balance = Math.abs(selected?.balance || 0);
  const maxPayment = Math.max(2000, Math.round(balance / 6));
  const minPayment = Math.max(50, Math.round(balance * 0.02));

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <Calculator className="w-6 h-6 text-[color:var(--gold)]" />
        <h1 className="font-heading text-3xl font-bold">Debt Payoff Simulator</h1>
      </div>
      <p className="text-[color:var(--text-secondary)] mb-8">See exactly when you're free — one debt or all of them.</p>

      <Tabs defaultValue="single" className="w-full">
        <TabsList className="mb-6 bg-[color:var(--bg-card)] border border-[rgba(212,175,55,0.15)]">
          <TabsTrigger data-testid="tab-single-debt" value="single" className="data-[state=active]:bg-[rgba(212,175,55,0.15)] data-[state=active]:text-[color:var(--gold)]">Single Debt</TabsTrigger>
          <TabsTrigger data-testid="tab-race-mode" value="race" className="data-[state=active]:bg-[rgba(212,175,55,0.15)] data-[state=active]:text-[color:var(--gold)]">
            <Trophy className="w-3 h-3 mr-1.5" /> Race Mode
          </TabsTrigger>
        </TabsList>

        <TabsContent value="single">
          <div className="flex gap-3 flex-wrap mb-8">
            {debts.map(d => (
              <button key={d.account_id} data-testid={`select-debt-${d.account_id}`} onClick={() => { setSelected(d); setPayment(Math.max(100, Math.round(Math.abs(d.balance) / 24))); }}
                className={`px-5 py-3 rounded-2xl border transition text-left ${selected?.account_id === d.account_id ? "border-[color:var(--gold)] bg-[rgba(212,175,55,0.08)]" : "border-[rgba(212,175,55,0.15)] hover:border-[rgba(212,175,55,0.35)]"}`}>
                <div className="text-sm font-medium">{d.name}</div>
                <div className="text-xs text-[color:var(--text-muted)]">${Math.abs(d.balance).toLocaleString()} · {d.apr}% APR</div>
              </button>
            ))}
          </div>

          {selected && (
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="glass-card p-8">
                <div className="text-xs uppercase tracking-widest text-[color:var(--text-muted)] mb-2">Monthly payment</div>
                <div className="font-mono text-5xl font-bold gold-gradient-text mb-6" data-testid="debt-monthly-payment">${payment}</div>
                <Slider data-testid="debt-payment-slider" value={[payment]} onValueChange={(v) => setPayment(v[0])}
                  min={minPayment} max={maxPayment} step={25} className="mb-3" />
                <div className="flex justify-between text-xs text-[color:var(--text-muted)] mb-6">
                  <span>Min ${minPayment}</span><span>Max ${maxPayment}</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[100, 250, 500, 1000].filter(x => x >= minPayment && x <= maxPayment).map(x => (
                    <button key={x} data-testid={`preset-${x}`} onClick={() => setPayment(x)}
                      className="px-4 py-2 rounded-xl border border-[rgba(212,175,55,0.15)] hover:border-[color:var(--gold)] text-sm transition">
                      ${x}/mo
                    </button>
                  ))}
                </div>
              </div>

              <motion.div key={JSON.stringify(result)} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-8">
                {result?.impossible ? (
                  <div className="text-center py-8">
                    <div className="text-[color:var(--terracotta)] font-heading text-xl mb-2">Payment too low</div>
                    <div className="text-sm text-[color:var(--text-secondary)]">You need at least ${result.min_payment}/mo to cover interest.</div>
                  </div>
                ) : result ? (
                  <>
                    <div className="grid grid-cols-2 gap-6 mb-6">
                      <div>
                        <div className="text-xs uppercase tracking-widest text-[color:var(--text-muted)] mb-2 flex items-center gap-1"><Calendar className="w-3 h-3" /> Debt-free by</div>
                        <div data-testid="debt-payoff-date" className="font-mono text-3xl font-bold gold-gradient-text">{result.payoff_date}</div>
                        <div className="text-sm text-[color:var(--text-secondary)] mt-1">{result.months} months</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-widest text-[color:var(--text-muted)] mb-2 flex items-center gap-1"><DollarSign className="w-3 h-3" /> Total interest</div>
                        <div data-testid="debt-total-interest" className="font-mono text-3xl font-bold text-[color:var(--terracotta)]">${result.total_interest.toLocaleString()}</div>
                        <div className="text-sm text-[color:var(--text-secondary)] mt-1">on ${result.starting_balance.toLocaleString()}</div>
                      </div>
                    </div>
                    <div className="border-t border-[rgba(212,175,55,0.1)] pt-4">
                      <div className="text-xs uppercase tracking-widest text-[color:var(--text-muted)] mb-3">Balance timeline</div>
                      <div className="space-y-2">
                        {result.timeline.slice(0, 8).map((t) => (
                          <div key={t.month} className="flex items-center gap-3 text-xs">
                            <span className="w-12 font-mono text-[color:var(--text-muted)]">M{t.month}</span>
                            <div className="flex-1 h-1.5 rounded-full bg-[rgba(255,255,255,0.05)] overflow-hidden">
                              <div className="h-full bg-gradient-to-r from-[#E06A3B] to-[#D4AF37]" style={{ width: `${(t.balance / result.starting_balance) * 100}%` }} />
                            </div>
                            <span className="w-20 font-mono text-right">${t.balance.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : <div className="text-center py-12 text-[color:var(--text-muted)]">Calculating...</div>}
              </motion.div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="race">
          <div className="glass-card p-8 mb-6">
            <div className="text-xs uppercase tracking-widest text-[color:var(--text-muted)] mb-2">Total monthly debt budget</div>
            <div className="font-mono text-5xl font-bold gold-gradient-text mb-6" data-testid="race-budget">${budget}</div>
            <Slider data-testid="race-budget-slider" value={[budget]} onValueChange={(v) => setBudget(v[0])}
              min={200} max={5000} step={50} className="mb-3" />
            <div className="flex justify-between text-xs text-[color:var(--text-muted)] mb-4">
              <span>$200</span><span>$5,000</span>
            </div>
            <div className="text-xs text-[color:var(--text-secondary)]">Total debt across {race?.debt_count || debts.length} accounts: <span className="font-mono font-semibold text-[color:var(--terracotta)]">${(race?.total_debt || 0).toLocaleString()}</span></div>
          </div>

          {race?.avalanche?.impossible ? (
            <div className="glass-card p-8 text-center">
              <div className="text-[color:var(--terracotta)] font-heading text-xl mb-2">Budget too low</div>
              <div className="text-sm text-[color:var(--text-secondary)]">Try at least ${race.avalanche.min_budget}/mo to cover minimums.</div>
            </div>
          ) : race && (
            <div className="grid lg:grid-cols-2 gap-6">
              <StrategyCard
                testId="strategy-avalanche"
                title="Avalanche"
                subtitle="Highest APR first"
                icon={Zap}
                color="var(--gold)"
                result={race.avalanche}
                isWinner={race.winner === "avalanche"}
                totalDebt={race.total_debt}
              />
              <StrategyCard
                testId="strategy-snowball"
                title="Snowball"
                subtitle="Smallest balance first"
                icon={Flame}
                color="var(--terracotta)"
                result={race.snowball}
                isWinner={race.winner === "snowball"}
                totalDebt={race.total_debt}
              />
            </div>
          )}

          {race && race.winner && !race.avalanche?.impossible && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              data-testid="race-winner-banner"
              className="glass-card p-6 mt-6 border-[rgba(212,175,55,0.4)] flex items-center gap-4">
              <Trophy className="w-8 h-8 text-[color:var(--gold)]" />
              <div>
                <div className="font-heading text-lg font-semibold">
                  {race.winner === "avalanche" ? "Avalanche" : "Snowball"} wins by ${Math.abs(race.avalanche.total_interest - race.snowball.total_interest).toLocaleString()}
                </div>
                <div className="text-sm text-[color:var(--text-secondary)]">
                  {race.winner === "avalanche" ? "You save more in interest by tackling the highest APR first." : "Faster wins keep you motivated — snowball costs slightly more but builds momentum."}
                </div>
              </div>
            </motion.div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StrategyCard({ testId, title, subtitle, icon: Icon, color, result, isWinner, totalDebt }) {
  return (
    <motion.div data-testid={testId} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className={`glass-card p-6 relative ${isWinner ? "border-[rgba(212,175,55,0.5)]" : ""}`}>
      {isWinner && <div className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#B38728] text-black text-xs font-semibold flex items-center gap-1"><Trophy className="w-3 h-3" /> Winner</div>}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `color-mix(in srgb, ${color} 15%, transparent)` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div>
          <div className="font-heading text-lg font-semibold">{title}</div>
          <div className="text-xs text-[color:var(--text-muted)]">{subtitle}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <div className="text-xs text-[color:var(--text-muted)] mb-1">Debt-free by</div>
          <div className="font-mono text-xl font-bold">{result.payoff_date}</div>
          <div className="text-xs text-[color:var(--text-secondary)]">{result.months} months</div>
        </div>
        <div>
          <div className="text-xs text-[color:var(--text-muted)] mb-1">Total interest</div>
          <div className="font-mono text-xl font-bold text-[color:var(--terracotta)]">${result.total_interest.toLocaleString()}</div>
        </div>
      </div>
      <div className="border-t border-[rgba(212,175,55,0.1)] pt-3 space-y-1.5">
        {result.timeline.slice(0, 6).map((t) => (
          <div key={t.month} className="flex items-center gap-2 text-xs">
            <span className="w-10 font-mono text-[color:var(--text-muted)]">M{t.month}</span>
            <div className="flex-1 h-1 rounded-full bg-[rgba(255,255,255,0.05)] overflow-hidden">
              <motion.div initial={{ width: "100%" }} animate={{ width: `${(t.balance / totalDebt) * 100}%` }}
                className="h-full rounded-full" style={{ background: color }} />
            </div>
            <span className="w-16 font-mono text-right">${t.balance.toLocaleString()}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}
